<template>
  <v-dialog v-model="visible" max-width="1200" persistent scrollable @keydown.esc="cancelForm">
    <v-card>
      <v-card-title class="info white--text">
        <v-icon left color="white">mdi-shield-lock</v-icon>
        Configurar Permisos por Dealer
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelForm">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="assignForm" v-model="validations.isValid">
          <!-- Búsqueda de usuario por email -->
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedUser"
                :items="filteredUsersForPermissions"
                :search-input.sync="emailSearch"
                :rules="validations.user"
                item-text="email"
                item-value="id"
                return-object
                label="Buscar usuario por email *"
                prepend-inner-icon="mdi-email-search"
                outlined
                clearable
                no-filter
                hide-details="auto"
                placeholder="Escribe el email del usuario..."
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center py-1">
                    <v-avatar size="32" color="primary lighten-4" class="mr-2">
                      <v-icon small color="primary">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
                      <div class="text-caption grey--text">{{ item.email }}</div>
                    </div>
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-avatar color="primary lighten-4">
                    <v-icon color="primary">mdi-account</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }} {{ item.lastname }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip x-small color="primary" outlined>
                      {{ item.dealership_name }}
                    </v-chip>
                  </v-list-item-action>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="text-center grey--text">
                        {{ emailSearch ? 'No se encontraron usuarios con ese email' : 'Escribe para buscar' }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Información del usuario seleccionado -->
          <v-row v-if="selectedUser">
            <v-col cols="12">
              <v-alert type="info" text dense class="mb-4">
                <div class="d-flex align-center">
                  <v-icon left>mdi-store</v-icon>
                  <strong>Dealer actual del usuario:</strong>
                  <span class="ml-2">{{ selectedUser.dealership_name }}</span>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Configuración de permisos por dealer -->
          <v-row v-if="selectedUser">
            <v-col cols="12">
              <v-divider class="mb-4"></v-divider>
              <div class="d-flex align-center justify-space-between mb-4">
                <h3 class="text-h6">Configurar permisos</h3>
                <div>
                  <v-btn small text color="primary" @click="expandAll" :disabled="loadingPermissions">
                    <v-icon small left>mdi-chevron-down</v-icon>
                    Expandir todo
                  </v-btn>
                  <v-btn small text color="primary" @click="collapseAll" :disabled="loadingPermissions">
                    <v-icon small left>mdi-chevron-up</v-icon>
                    Colapsar todo
                  </v-btn>
                </div>
              </div>

              <!-- Loader mientras se cargan los permisos -->
              <v-row v-if="loadingPermissions">
                <v-col cols="12" class="text-center py-8">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                  ></v-progress-circular>
                  <p class="text-subtitle-1 grey--text mt-3">
                    Cargando permisos disponibles...
                  </p>
                </v-col>
              </v-row>

              <!-- Mensaje si no hay otros dealers -->
              <v-alert v-else-if="availableDealers.length === 0" type="warning" dense text>
                <v-icon left>mdi-information</v-icon>
                No hay otros dealers en el grupo además del dealer actual del usuario.
              </v-alert>

              <!-- Mensaje si no hay permisos disponibles -->
              <v-alert v-else-if="permissionCategories.length === 0" type="warning" dense text>
                <v-icon left>mdi-alert</v-icon>
                No se encontraron permisos disponibles. Por favor, contacta al administrador.
              </v-alert>
              
              <v-expansion-panels v-else v-model="expandedPanels" multiple accordion>
                <v-expansion-panel
                  v-for="dealer in availableDealers"
                  :key="dealer.id"
                >
                  <v-expansion-panel-header>
                    <div class="d-flex align-center">
                      <v-icon left color="primary">mdi-store</v-icon>
                      <strong>{{ dealer.name }}</strong>
                      <v-chip
                        x-small
                        :color="getDealerPermissionCount(dealer.id) > 0 ? 'success' : 'grey'"
                        text-color="white"
                        class="ml-3"
                      >
                        {{ getDealerPermissionCount(dealer.id) }} permiso(s)
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-card flat class="pa-3">
                      <v-row>
                        <v-col cols="12" class="pb-0">
                          <div class="d-flex justify-space-between align-center mb-3">
                            <span class="text-subtitle-2 grey--text">Selecciona los permisos para este dealer:</span>
                            <v-btn
                              x-small
                              text
                              color="error"
                              @click="clearDealerPermissions(dealer.id)"
                              v-if="dealerPermissions[dealer.id].length > 0"
                            >
                              <v-icon x-small left>mdi-close-circle</v-icon>
                              Limpiar todos
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Categorías de permisos -->
                      <v-expansion-panels
                        accordion
                        multiple
                        class="mb-2"
                        v-model="expandedCategoryPanels[dealer.id]"
                      >
                        <v-expansion-panel
                          v-for="category in permissionCategories"
                          :key="`${dealer.id}-${category.key}`"
                        >
                          <v-expansion-panel-header class="py-2">
                            <div class="d-flex align-center">
                              <v-icon :color="category.color" small left>{{ category.icon }}</v-icon>
                              <div>
                                <div class="text-body-2 font-weight-medium">{{ category.title }}</div>
                                <div class="text-caption text--secondary">{{ category.description }}</div>
                              </div>
                            </div>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div class="permission-grid">
                              <v-checkbox
                                v-for="permission in category.permissions"
                                :key="`${dealer.id}-${permission.value}`"
                                v-model="dealerPermissions[dealer.id]"
                                :value="permission.value"
                                :label="permission.label"
                                dense
                                hide-details
                                class="permission-checkbox"
                              ></v-checkbox>
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>

                      <v-alert v-if="dealerPermissions[dealer.id].length > 0" type="success" dense text class="mt-3 mb-0">
                        <v-icon left small>mdi-check-circle</v-icon>
                        {{ dealerPermissions[dealer.id].length }} permiso(s) seleccionado(s) para {{ dealer.name }}
                      </v-alert>
                    </v-card>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>

          <!-- Mensaje cuando no hay usuario seleccionado -->
          <v-row v-else>
            <v-col cols="12" class="text-center py-8">
              <v-icon size="64" color="grey lighten-2">mdi-account-search</v-icon>
              <p class="text-subtitle-1 grey--text mt-3">
                Selecciona un usuario para configurar sus permisos en cada dealer del grupo
              </p>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn :disabled="loading" text @click="cancelForm">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          :disabled="saveIsDisabled"
          :loading="loading"
          @click="saveForm"
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar Permisos
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UserService from '@/services/UserService'

export default {
  name: 'UserPermissionsDialog',
  props: {
    users: {
      type: Array,
      default: () => []
    },
    dealers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      visible: true,
      loading: false,
      loadingPermissions: false, // Ya no necesita cargar async
      selectedUser: null,
      emailSearch: '',
      dealerPermissions: {},
      initialDealerPermissions: {}, // Para detectar cambios, incluyendo eliminación de permisos
      expandedPanels: [],
      expandedCategoryPanels: {},
      
      validations: {
        isValid: true,
        user: [
          v => !!v || 'Debe seleccionar un usuario.'
        ]
      },

      // ---- Misma estructura que PermissionsForm ----
      userPermissions: [
        { value: 'actualizar_usuario', label: 'Actualizar usuario' },
        { value: 'agregar_usuario_normal', label: 'Agregar usuario normal' },
        { value: 'eliminar_usuario', label: 'Eliminar usuario' }
      ],
      vehiclePermissions: [
        { value: 'crear_vehiculo', label: 'Crear vehículo' },
        { value: 'editar_vehiculo', label: 'Editar vehículo' },
        { value: 'eliminar_vehiculo', label: 'Eliminar vehículo' },
        { value: 'asignar_todo', label: 'Asignar TO-DO' },
        { value: 'asignar_estado_proceso', label: 'Asignar Estado Proceso' },
        { value: 'exportar_vehiculos', label: 'Exportar Vehículos' }
      ],
      movementPermissions: [
        { value: 'crear_movimiento', label: 'Crear movimiento' },
        { value: 'editar_movimiento', label: 'Editar movimiento' },
        { value: 'eliminar_movimiento', label: 'Eliminar movimiento' },
        { value: 'aceptar_movimiento', label: 'Aceptar movimiento' },
        { value: 'view_chekbox_validacion', label: 'Ver checkbox de validación' },
        { value: 'usage_chekbox_validacion', label: 'Usar checkbox de validación' },
        { value: 'create_movement_excepcional', label: 'Crear movimiento excepcional' }

      ],
      cesionPermissions: [
        { value: 'crear_cesion', label: 'Crear cesión' },
        { value: 'editar_cesion', label: 'Editar cesión' },
        { value: 'eliminar_cesion', label: 'Eliminar cesión' },
        { value: 'aceptar_cesion', label: 'Aceptar cesión' }
      ],
      locationPermissions: [
        { value: 'crear_ubicacion', label: 'Crear ubicación' },
        { value: 'editar_ubicacion', label: 'Editar ubicación' },
        { value: 'eliminar_ubicacion', label: 'Eliminar ubicación' }
      ],
      driverPermissions: [
        { value: 'crear_conductor', label: 'Crear conductor' },
        { value: 'editar_conductor', label: 'Editar conductor' }
      ],
      cranePermissions: [
        { value: 'crear_grua', label: 'Crear grúa' },
        { value: 'editar_grua', label: 'Editar grúa' }
      ],
      systemPermissions: [
        { value: 'manage_dealer', label: 'Administrar dealer' },
        { value: 'manage_equipamiento_config', label: 'Configurar equipamiento' },
        { value: 'manage_notifications', label: 'Administrar notificaciones' },
        { value: 'crear_todo', label: 'Crear TO-DO' },
        { value: 'editar_todo', label: 'Editar TO-DO' },
        { value: 'eliminar_todo', label: 'Eliminar TO-DO' },
        { value: 'crear_estado_proceso', label: 'Crear Estado Proceso' },
        { value: 'editar_estado_proceso', label: 'Editar Estado Proceso' },
        { value: 'eliminar_estado_proceso', label: 'Eliminar Estado Proceso' }
      ],
      importPermissions: [
        { value: 'importacion_masiva_excel', label: 'Importación masiva Excel' }
      ],
      otherPermissions: [
        { value: 'descargar_comodato', label: 'Descargar comodato' },
        { value: 'editar_comodato', label: 'Editar clausulado comodato' }
      ]
    }
  },

  computed: {
    // Misma estructura de categorías que PermissionsForm
    permissionCategories() {
      return [
        {
          key: 'users',
          title: 'Gestión de Usuarios',
          description: 'Permisos para administrar cuentas de usuario',
          icon: 'mdi-account-group',
          color: 'blue',
          permissions: this.userPermissions
        },
        {
          key: 'vehicles',
          title: 'Gestión de Vehículos',
          description: 'Permisos para crear, editar y eliminar vehículos',
          icon: 'mdi-car-multiple',
          color: 'green',
          permissions: this.vehiclePermissions
        },
        {
          key: 'movements',
          title: 'Gestión de Movimientos',
          description: 'Permisos para gestionar movimientos y transportes',
          icon: 'mdi-truck-delivery',
          color: 'orange',
          permissions: this.movementPermissions
        },
        {
          key: 'cesion',
          title: 'Gestión de Cesiones',
          description: 'Permisos para gestionar cesiones',
          icon: 'mdi-swap-horizontal',
          color: 'orange',
          permissions: this.cesionPermissions
        },
        {
          key: 'locations',
          title: 'Gestión de Ubicaciones',
          description: 'Permisos para administrar ubicaciones y sedes',
          icon: 'mdi-map-marker-multiple',
          color: 'purple',
          permissions: this.locationPermissions
        },
        {
          key: 'drivers',
          title: 'Gestión de Conductores',
          description: 'Permisos para administrar conductores',
          icon: 'mdi-account-tie',
          color: 'teal',
          permissions: this.driverPermissions
        },
        {
          key: 'cranes',
          title: 'Gestión de Grúas',
          description: 'Permisos para administrar empresas de grúas',
          icon: 'mdi-crane',
          color: 'brown',
          permissions: this.cranePermissions
        },
        {
          key: 'system',
          title: 'Sistema y Configuración',
          description: 'Permisos administrativos del sistema',
          icon: 'mdi-cog-outline',
          color: 'grey darken-1',
          permissions: this.systemPermissions
        },
        {
          key: 'import',
          title: 'Importación de Datos',
          description: 'Permisos para importar datos masivamente',
          icon: 'mdi-file-excel-box',
          color: 'success',
          permissions: this.importPermissions
        },
        {
          key: 'other',
          title: 'Permisos Especiales',
          description: 'Permisos adicionales y específicos',
          icon: 'mdi-star-circle',
          color: 'amber darken-2',
          permissions: this.otherPermissions
        }
      ]
    },
    permissionsPayload() {
      if (!this.selectedUser) return null

      const dealerPermissionsData = []

      Object.entries(this.dealerPermissions).forEach(([dealerId, permissions]) => {
        dealerPermissionsData.push({
          dealer_id: parseInt(dealerId),
          permissions
        })
      })

      return {
        user_id: this.selectedUser.id,
        current_dealer_id: this.selectedUser.dealership_id,
        dealer_permissions: dealerPermissionsData
      }
    },

    filteredUsersForPermissions() {
      if (!this.emailSearch || this.emailSearch.length < 2) {
        return []
      }
      const searchLower = this.emailSearch.toLowerCase()
      return this.users.filter(user =>
        user.email && user.email.toLowerCase().includes(searchLower)
      )
    },

    availableDealers() {
      if (!this.selectedUser) {
        return this.dealers
      }
      return this.dealers.filter(dealer => dealer.id !== this.selectedUser.dealership_id)
    },

    hasPermissionsChanges() {
      // Detectar si hay cambios comparando con los permisos iniciales
      // Esto permite detectar cuando se eliminan todos los permisos
      const dealerIds = new Set([
        ...Object.keys(this.dealerPermissions),
        ...Object.keys(this.initialDealerPermissions)
      ])

      for (const dealerId of dealerIds) {
        const current = this.dealerPermissions[dealerId] || []
        const initial = this.initialDealerPermissions[dealerId] || []
        
        // Si los arrays tienen diferente longitud, hay cambios
        if (current.length !== initial.length) {
          return true
        }
        
        // Si tienen la misma longitud, verificar que tengan los mismos elementos
        const currentSorted = [...current].sort()
        const initialSorted = [...initial].sort()
        
        if (currentSorted.join(',') !== initialSorted.join(',')) {
          return true
        }
      }

      return false
    },

    saveIsDisabled() {
      return this.loading || !this.selectedUser || !this.hasPermissionsChanges
    }
  },

  watch: {
    selectedUser(newUser) {
      if (newUser) {
        this.initializeDealerPermissions()
        this.loadExistingPermissions(newUser)
      }
    }
  },

  created() {
    this.initializeDealerPermissions()
  },

  methods: {
    initializeDealerPermissions() {
    this.dealerPermissions = {}
    this.expandedCategoryPanels = {}
    // Inicializar solo con los dealers disponibles (excluyendo el del usuario)
    const dealers = this.selectedUser
      ? this.dealers.filter(d => d.id !== this.selectedUser.dealership_id)
      : this.dealers

    dealers.forEach(dealer => {
      this.$set(this.dealerPermissions, dealer.id, [])
      this.$set(this.expandedCategoryPanels, dealer.id, [])
    })
    },

    async loadExistingPermissions(user) {
    try {
      this.loadingPermissions = true

      // Para cada dealer disponible (excluyendo el propio del usuario)
      const dealers = this.dealers.filter(d => d.id !== user.dealership_id)

      const promises = dealers.map(dealer =>
        UserService.getUserDealershipPermissions({
          user_id: user.id,
          dealership_id: dealer.id
        })
      )

      const results = await Promise.all(promises)

      // Limpiar permisos iniciales
      this.initialDealerPermissions = {}

      results.forEach((response, index) => {
        const dealer = dealers[index]
        const permissions = response.data?.data?.permission_list || []
        
        // Guardar en permisos actuales
        if (permissions.length > 0) {
          this.$set(this.dealerPermissions, dealer.id, permissions)
        }
        
        // Guardar en permisos iniciales (siempre, incluso si está vacío)
        this.$set(this.initialDealerPermissions, dealer.id, [...permissions])
      })
    } catch (error) {
      console.error('Error cargando permisos existentes:', error)
      this.$toast.error('Error al cargar los permisos existentes del usuario')
    } finally {
      this.loadingPermissions = false
    }
  },
    getDealerPermissionCount(dealerId) {
      return this.dealerPermissions[dealerId]?.length || 0
    },

    clearDealerPermissions(dealerId) {
      this.$set(this.dealerPermissions, dealerId, [])
    },

    expandAll() {
      this.expandedPanels = this.availableDealers.map((_, index) => index)
      this.availableDealers.forEach(dealer => {
        this.$set(
          this.expandedCategoryPanels,
          dealer.id,
          this.permissionCategories.map((_, index) => index)
        )
      })
    },

    collapseAll() {
      this.expandedPanels = []
      this.availableDealers.forEach(dealer => {
        this.$set(this.expandedCategoryPanels, dealer.id, [])
      })
    },

    resetForm() {
      this.selectedUser = null
      this.emailSearch = ''
      this.initializeDealerPermissions()
      this.initialDealerPermissions = {}
      this.expandedPanels = []
      this.expandedCategoryPanels = {}
      if (this.$refs.assignForm) {
        this.$refs.assignForm.resetValidation()
      }
    },

    cancelForm() {
      this.resetForm()
      this.$emit('cancel')
    },

    saveForm() {
      this.$refs.assignForm.validate()

      if (!this.validations.isValid) return
      if (!this.selectedUser) {
        this.$toast.warning('Debes seleccionar un usuario')
        return
      }
      if (!this.hasPermissionsChanges) {
        this.$toast.warning('No se detectaron cambios en los permisos')
        return
      }

      this.$emit('save', this.permissionsPayload)
      this.resetForm()
    }
  }
}
</script>

<style scoped>
.permission-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 8px;
}

.permission-checkbox {
  margin: 0 !important;
}
</style>
